import { httpClient } from "src/shared/services/httpClient";
import { AxiosResponse } from "axios";
import {
  ActiveComplaintsResponse,
  AdminComplaintsResponse,
  Chamber,
  Complainee,
  ComplaintDetailsResponse,
  ComplaintHistoryResponse,
  Organization,
  PastComplaintsResponse,
  PendingComplaintsResponse,
  Status,
  SupervisoryTeamComplaintsResponse,
  TComplaintForm,
  ViolationType,
} from "src/complaint/types";
import { format } from "date-fns";

export const getComplaintFormInfo = (): Promise<
  AxiosResponse<TComplaintForm>
> => httpClient.get("complaint/getComplaintFormInfo");

export const getComplaintForChamber = (params: {
  complaintId: number;
}): Promise<AxiosResponse<ComplaintDetailsResponse>> =>
  httpClient.get("complaint/getComplaintForChamber", { params });

export const getComplaineeInfo = ({
  gemiNumber,
}: {
  gemiNumber: number;
}): Promise<AxiosResponse<Complainee>> =>
  httpClient.get(
    "complaint/getComplaineeInfo?gemiNumber=" + gemiNumber.toString(),
  );

export const submitComplaint = ({
  profession,
  complaineeGemiNumber,
  violationType,
  complaintDescription,
  complaintFileName,
  violationYear,
}: {
  profession?: string;
  complaineeGemiNumber: string;
  violationType: number;
  complaintDescription: string;
  complaintFileName?: any;
  violationYear?: number;
}) => {
  const formData = new FormData();
  if (profession) {
    formData.append("profession", profession);
  }
  formData.append("complaineeGemiNumber", complaineeGemiNumber);
  formData.append("violationType", violationType.toString());
  formData.append("complaintDescription", complaintDescription);
  if (complaintFileName && complaintFileName[0]) {
    formData.append("complaintFileName", complaintFileName[0]);
  }
  if (violationYear) {
    formData.append("financialYear", violationYear.toString());
  }
  return httpClient.post("complaint/submitComplaint", formData);
};

export const getComplainantHistory = (
  length: number,
  start: number,
): Promise<AxiosResponse<ComplaintHistoryResponse>> =>
  httpClient.get(
    "complaint/getComplainantHistory?start=" +
      start.toString() +
      "&length=" +
      length.toString(),
  );

export const getComplaint = (
  complaintId: number,
): Promise<AxiosResponse<ComplaintDetailsResponse>> =>
  httpClient.get(
    "complaint/getComplaint?complaintId=" + complaintId.toString(),
  );

export const getPendingComplaints = (
  length: number,
  start: number,
  sortingData: string,
  violationTypeId: number | null | undefined,
  gemiNumber: number | null | undefined,
  complaintId: number | null | undefined,
  fromDate: Date | null | undefined,
  toDate: Date | null | undefined,
): Promise<AxiosResponse<PendingComplaintsResponse>> => {
  const formData = new FormData();
  formData.append("start", start.toString());
  formData.append("length", length.toString());
  formData.append("sortingData", sortingData.toString());
  if (violationTypeId) {
    formData.append("violationTypeId", violationTypeId.toString());
  }
  if (gemiNumber) {
    formData.append("gemiNumber", gemiNumber.toString());
  }
  if (complaintId) {
    formData.append("complaintId", complaintId.toString());
  }
  if (fromDate) {
    formData.append("fromDate", format(fromDate, "yyyy-MM-dd"));
  }
  if (toDate) {
    formData.append("toDate", format(toDate, "yyyy-MM-dd"));
  }
  return httpClient.post("complaint/getPendingComplaintsForChamber", formData);
};

export const getActiveComplaints = (
  length: number,
  start: number,
  sortingData: string,
  fetchForEntireChamber: boolean,
  violationTypeId: number | null | undefined,
  gemiNumber: number | null | undefined,
  complaintId: number | null | undefined,
  chamberUser: number | null | undefined,
  fromDate: Date | null | undefined,
  toDate: Date | null | undefined,
): Promise<AxiosResponse<ActiveComplaintsResponse>> => {
  const formData = new FormData();
  formData.append("start", start.toString());
  formData.append("length", length.toString());
  formData.append("sortingData", sortingData);
  formData.append("fetchForEntireChamber", fetchForEntireChamber.toString());
  if (violationTypeId) {
    formData.append("violationTypeId", violationTypeId.toString());
  }
  if (gemiNumber) {
    formData.append("gemiNumber", gemiNumber.toString());
  }
  if (complaintId) {
    formData.append("complaintId", complaintId.toString());
  }
  if (chamberUser) {
    formData.append("chamberUser", chamberUser.toString());
  }
  if (fromDate) {
    formData.append("fromDate", format(fromDate, "yyyy-MM-dd"));
  }
  if (toDate) {
    formData.append("toDate", format(toDate, "yyyy-MM-dd"));
  }
  return httpClient.post("complaint/getActiveComplaintsForChamber", formData);
};

export const getPastComplaints = (
  length: number,
  start: number,
  sortingData: string,
  fetchForEntireChamber: boolean,
  violationTypeId: number | null | undefined,
  gemiNumber: number | null | undefined,
  complaintId: number | null | undefined,
  chamberUser: number | null | undefined,
  statusId: number | null | undefined,
  fromDate: Date | null | undefined,
  toDate: Date | null | undefined,
  completedDateFrom: Date | null | undefined,
  completedDateTo: Date | null | undefined,
): Promise<AxiosResponse<PastComplaintsResponse>> => {
  const formData = new FormData();
  formData.append("start", start.toString());
  formData.append("length", length.toString());
  formData.append("sortingData", sortingData.toString());
  formData.append("fetchForEntireChamber", fetchForEntireChamber.toString());
  if (violationTypeId) {
    formData.append("violationTypeId", violationTypeId.toString());
  }
  if (gemiNumber) {
    formData.append("gemiNumber", gemiNumber.toString());
  }
  if (complaintId) {
    formData.append("complaintId", complaintId.toString());
  }
  if (chamberUser) {
    formData.append("chamberUser", chamberUser.toString());
  }
  if (statusId) {
    formData.append("statusId", statusId.toString());
  }
  if (fromDate) {
    formData.append("fromDate", format(fromDate, "yyyy-MM-dd"));
  }
  if (toDate) {
    formData.append("toDate", format(toDate, "yyyy-MM-dd"));
  }
  if (completedDateFrom) {
    formData.append(
      "completedDateFrom",
      format(completedDateFrom, "yyyy-MM-dd"),
    );
  }
  if (completedDateTo) {
    formData.append("completedDateTo", format(completedDateTo, "yyyy-MM-dd"));
  }
  return httpClient.post("complaint/getPastComplaintsForChamber", formData);
};

export const getChamberUsers = (): Promise<AxiosResponse<Chamber[]>> =>
  httpClient.get("complaint/getChamberUsers");

export const getViolationTypes = (): Promise<AxiosResponse<ViolationType[]>> =>
  httpClient.get("complaint/getViolationTypes");

export const getComplaintStatuses = (
  isSystemAdmin: boolean,
): Promise<AxiosResponse<Status[]>> =>
  httpClient.get(
    `complaint/${isSystemAdmin ? "getComplaintStatuses" : "getComplaintStatusesForPastApplications"}`,
  );

export const getOAuthHandleForComplaint = (
  token: string | null | undefined,
  oauth_verifier: string | null | undefined,
) => {
  return httpClient.get(
    `complaint/oauthHandleForComplaint?oauth_token=${token}&oauth_verifier=${oauth_verifier}`,
  );
};

export const forwardComplaintApplication = ({
  complaintId,
  assigneeId,
  message,
}: {
  complaintId: string;
  assigneeId: string;
  message?: string;
}) => {
  const formData = new FormData();
  formData.append("complaintId", complaintId);
  formData.append("assigneeId", assigneeId);
  if (message) {
    formData.append("message", message);
  }
  return httpClient.post("complaint/assignComplaintToChamberUser", formData);
};

export const finalizeComplaintApplication = ({
  complaintId,
  mode,
  chamberResponse,
}: {
  complaintId: string;
  mode: string;
  chamberResponse?: string;
}) => {
  const formData = new FormData();
  formData.append("complaintId", complaintId);
  formData.append("mode", mode);
  if (chamberResponse) {
    formData.append("chamberResponse", chamberResponse);
  }
  return httpClient.post("complaint/finalizeComplaintApplication", formData);
};

export const submitChamberNotes = ({
  notes,
  complaintId,
}: {
  notes: string;
  complaintId: string;
}) => {
  const formData = new FormData();
  formData.append("notes", notes);
  formData.append("complaintId", complaintId);
  return httpClient.post("complaint/submitChamberNotes", formData);
};

export const getAdminComplaints = (
  length: number,
  start: number,
  sortingData: string,
  violationTypeId: number | null | undefined,
  gemiNumber: number | null | undefined,
  complaintId: number | null | undefined,
  statusId: number | null | undefined,
  fromDate: Date | null | undefined,
  toDate: Date | null | undefined,
  completedDateFrom: Date | null | undefined,
  completedDateTo: Date | null | undefined,
  organization: number | null | undefined,
): Promise<AxiosResponse<AdminComplaintsResponse>> => {
  const formData = new FormData();
  formData.append("start", start.toString());
  formData.append("length", length.toString());
  formData.append("sortingData", sortingData.toString());
  if (violationTypeId) {
    formData.append("violationTypeId", violationTypeId.toString());
  }
  if (gemiNumber) {
    formData.append("gemiNumber", gemiNumber.toString());
  }
  if (complaintId) {
    formData.append("complaintId", complaintId.toString());
  }
  if (statusId) {
    formData.append("statusId", statusId.toString());
  }
  if (fromDate) {
    formData.append("fromDate", format(fromDate, "yyyy-MM-dd"));
  }
  if (toDate) {
    formData.append("toDate", format(toDate, "yyyy-MM-dd"));
  }
  if (completedDateFrom) {
    formData.append(
      "completedDateFrom",
      format(completedDateFrom, "yyyy-MM-dd"),
    );
  }
  if (completedDateTo) {
    formData.append("completedDateTo", format(completedDateTo, "yyyy-MM-dd"));
  }
  if (organization) {
    formData.append("organization", organization.toString());
  }
  return httpClient.post("complaint/getComplaintsForAdmin", formData);
};

export const getOrganizationsForAdmin = (): Promise<
  AxiosResponse<Organization[]>
> => httpClient.get("complaint/getOrganizationsForAdmin");

export const getSupervisoryTeamComplaints = (
  length: number,
  start: number,
  sortingData: string,
  violationTypeId: number | null | undefined,
  gemiNumber: number | null | undefined,
  complaintId: number | null | undefined,
  statusId: number | null | undefined,
  fromDate: Date | null | undefined,
  toDate: Date | null | undefined,
  completedDateFrom: Date | null | undefined,
  completedDateTo: Date | null | undefined,
  organization: number | null | undefined,
): Promise<AxiosResponse<SupervisoryTeamComplaintsResponse>> => {
  const formData = new FormData();
  formData.append("start", start.toString());
  formData.append("length", length.toString());
  formData.append("sortingData", sortingData.toString());
  if (violationTypeId) {
    formData.append("violationTypeId", violationTypeId.toString());
  }
  if (gemiNumber) {
    formData.append("gemiNumber", gemiNumber.toString());
  }
  if (complaintId) {
    formData.append("complaintId", complaintId.toString());
  }
  if (statusId) {
    formData.append("statusId", statusId.toString());
  }
  if (fromDate) {
    formData.append("fromDate", format(fromDate, "yyyy-MM-dd"));
  }
  if (toDate) {
    formData.append("toDate", format(toDate, "yyyy-MM-dd"));
  }
  if (completedDateFrom) {
    formData.append(
      "completedDateFrom",
      format(completedDateFrom, "yyyy-MM-dd"),
    );
  }
  if (completedDateTo) {
    formData.append("completedDateTo", format(completedDateTo, "yyyy-MM-dd"));
  }
  if (organization) {
    formData.append("organization", organization.toString());
  }
  return httpClient.post(
    "supervisoryTeam/getComplaintsForSupervisoryTeam",
    formData,
  );
};
